export interface ClientError {
  message: string;
  stack?: string;
  componentStack?: string;
  source: 'error-boundary' | 'unhandled-error' | 'unhandled-rejection';
  pageUrl: string;
  userAgent: string;
  timestamp: string;
}

const MAX_ERRORS_PER_MINUTE = 10;
const FLUSH_DELAY_MS = 5000;
const MAX_MESSAGE_LENGTH = 4000;
const MAX_STACK_LENGTH = 8000;

const SENSITIVE_PARAMS = ['token', 'key', 'secret', 'password', 'auth', 'code', 'session'];

class ErrorReporter {
  private buffer: ClientError[] = [];
  private errorCountThisMinute = 0;
  private minuteResetTimer: ReturnType<typeof setTimeout> | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private isInitialized = false;

  initialize(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;

    window.addEventListener('error', (event: ErrorEvent) => {
      this.captureError({
        message: event.message || 'Unknown error',
        stack: event.error?.stack,
        source: 'unhandled-error',
        pageUrl: this.sanitizeUrl(window.location.href),
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      this.captureError({
        message: reason?.message || String(reason) || 'Unhandled promise rejection',
        stack: reason?.stack,
        source: 'unhandled-rejection',
        pageUrl: this.sanitizeUrl(window.location.href),
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });
    });
  }

  captureComponentError(error: Error, componentStack?: string): void {
    this.captureError({
      message: error.message,
      stack: error.stack,
      componentStack: componentStack,
      source: 'error-boundary',
      pageUrl: this.sanitizeUrl(window.location.href),
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  }

  private captureError(error: ClientError): void {
    if (this.errorCountThisMinute >= MAX_ERRORS_PER_MINUTE) return;
    this.errorCountThisMinute++;

    if (!this.minuteResetTimer) {
      this.minuteResetTimer = setTimeout(() => {
        this.errorCountThisMinute = 0;
        this.minuteResetTimer = null;
      }, 60_000);
    }

    error.message = error.message?.substring(0, MAX_MESSAGE_LENGTH) || '';
    error.stack = error.stack?.substring(0, MAX_STACK_LENGTH);
    error.componentStack = error.componentStack?.substring(0, MAX_MESSAGE_LENGTH);

    this.buffer.push(error);

    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => this.flush(), FLUSH_DELAY_MS);
    }
  }

  private async flush(): Promise<void> {
    this.flushTimer = null;
    if (this.buffer.length === 0) return;

    const errors = [...this.buffer];
    this.buffer = [];

    try {
      const { apiClient } = await import('./api');
      await apiClient.reportClientErrors(errors);
    } catch {
      // Silently fail — never create error loops
      console.warn('[ErrorReporter] Failed to send error report');
    }
  }

  private sanitizeUrl(url: string): string {
    try {
      const parsed = new URL(url);
      SENSITIVE_PARAMS.forEach((param) => parsed.searchParams.delete(param));
      return parsed.pathname + (parsed.search || '');
    } catch {
      return url.split('?')[0] || url;
    }
  }
}

export const errorReporter = new ErrorReporter();
